import { useState } from "react";
import { motion } from "framer-motion";
import {
    User,
    Phone,
    Mail,
    BarChart3,
    CalendarDays,
    CheckCircle2,
    Loader2,
} from "lucide-react";
import Reveal from "./Reveal";
import CtaButton from "./CtaButton";
import { trackCompleteRegistration } from "@/config";

const inputCls =
    "w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3.5 text-sm text-white placeholder-white/35 outline-none transition-colors duration-300 focus:border-neon/60 focus:bg-white/[0.06]";

const RegistrationSection = () => {
    const [state, setState] = useState("idle");
    const [errorMsg, setErrorMsg] = useState("");

    const onSubmit = async (e) => {
        e.preventDefault();
        const form = e.currentTarget;
        const data = new FormData(form);
        const email = (data.get("email") || "").toString().trim();
        setState("sending");
        setErrorMsg("");
        try {
            const res = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/registrations`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        name: (data.get("name") || "").toString().trim(),
                        phone: (data.get("phone") || "").toString().trim(),
                        email: email || null,
                        experience: (data.get("experience") || "").toString(),
                        preferred_day: (data.get("preferred_day") || "").toString(),
                        consent: true,
                    }),
                },
            );
            if (!res.ok) {
                let detail = "Sila semak semula maklumat anda.";
                try {
                    const body = await res.json();
                    if (typeof body.detail === "string") detail = body.detail;
                } catch (_) {}
                throw new Error(detail);
            }
            setState("done");
            trackCompleteRegistration();
        } catch (err) {
            setErrorMsg(
                err instanceof TypeError
                    ? "Sambungan bermasalah. Sila cuba lagi."
                    : err.message,
            );
            setState("error");
        }
    };

    return (
        <section
            id="registration-form"
            data-testid="registration-section"
            className="relative scroll-mt-20 py-24 md:scroll-mt-24 md:py-32"
        >
            <div className="pointer-events-none absolute -left-32 top-20 h-[420px] w-[420px] rounded-full bg-electric/15 blur-3xl" />
            <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:px-8">
                <Reveal>
                    <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-neon">
                        Borang pendaftaran — pilihan sahaja
                    </p>
                    <h2 className="mt-5 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl md:text-5xl">
                        REGISTER FOR THE{" "}
                        <span className="text-duo-grad">NEXT WEBINAR</span>
                    </h2>
                    <p className="mt-5 max-w-xl leading-relaxed text-white/70">
                        Isi maklumat anda dan pihak admin akan menghubungi anda
                        untuk maklumat penyertaan webinar.
                    </p>
                    <div className="mt-8 flex flex-wrap gap-3">
                        {[
                            "EVERY MONDAY & TUESDAY",
                            "8:30 PM – 10:00 PM",
                            "LIVE VIA ZOOM",
                        ].map((t) => (
                            <span
                                key={t}
                                className="glass rounded-full px-4 py-2 font-mono text-[10px] uppercase tracking-[0.16em] text-white/85"
                            >
                                {t}
                            </span>
                        ))}
                    </div>
                </Reveal>

                <Reveal delay={0.1}>
                    <div
                        data-testid="registration-card"
                        className="neon-form card-luxe rounded-3xl p-6 md:p-9"
                    >
                        {state === "done" ? (
                            <motion.div
                                initial={{ opacity: 0, scale: 0.96 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.5 }}
                                data-testid="registration-success"
                                className="flex flex-col items-center gap-5 py-8 text-center"
                            >
                                <CheckCircle2 size={56} className="text-neon" />
                                <h3 className="font-display text-xl font-extrabold leading-snug text-white md:text-2xl">
                                    Terima kasih! Pendaftaran anda telah
                                    diterima. Sila PM admin Telegram untuk
                                    mendapatkan maklumat lanjut mengenai sesi
                                    webinar.
                                </h3>
                                <CtaButton
                                    location="Post-Registration"
                                    testId="post-registration-telegram-button"
                                >
                                    Hubungi Admin Telegram
                                </CtaButton>
                            </motion.div>
                        ) : (
                            <form
                                onSubmit={onSubmit}
                                data-testid="registration-form-element"
                                className="space-y-5"
                            >
                                <div>
                                    <label
                                        htmlFor="reg-name"
                                        className="mb-2 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/60"
                                    >
                                        <User size={12} className="text-neon" />
                                        Nama Penuh *
                                    </label>
                                    <input
                                        id="reg-name"
                                        name="name"
                                        type="text"
                                        required
                                        maxLength={120}
                                        placeholder="Contoh: Ahmad bin Ali"
                                        data-testid="registration-name-input"
                                        className={inputCls}
                                    />
                                </div>

                                <div>
                                    <label
                                        htmlFor="reg-phone"
                                        className="mb-2 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/60"
                                    >
                                        <Phone size={12} className="text-neon" />
                                        No. Telefon *
                                    </label>
                                    <input
                                        id="reg-phone"
                                        name="phone"
                                        type="tel"
                                        required
                                        minLength={8}
                                        maxLength={30}
                                        placeholder="Contoh: 0123456789"
                                        data-testid="registration-phone-input"
                                        className={inputCls}
                                    />
                                </div>

                                <div>
                                    <label
                                        htmlFor="reg-email"
                                        className="mb-2 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/60"
                                    >
                                        <Mail size={12} className="text-neon" />
                                        Email (pilihan)
                                    </label>
                                    <input
                                        id="reg-email"
                                        name="email"
                                        type="email"
                                        placeholder="contoh@email.com"
                                        data-testid="registration-email-input"
                                        className={inputCls}
                                    />
                                </div>

                                <div>
                                    <label
                                        htmlFor="reg-experience"
                                        className="mb-2 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/60"
                                    >
                                        <BarChart3
                                            size={12}
                                            className="text-neon"
                                        />
                                        Pengalaman Trading *
                                    </label>
                                    <select
                                        id="reg-experience"
                                        name="experience"
                                        required
                                        defaultValue=""
                                        data-testid="registration-experience-select"
                                        className={`${inputCls} cursor-pointer [&>option]:bg-[#141020]`}
                                    >
                                        <option value="" disabled>
                                            Pilih tahap pengalaman
                                        </option>
                                        <option value="Beginner">Beginner</option>
                                        <option value="Less than 1 year">
                                            Less than 1 year
                                        </option>
                                        <option value="1–3 years">1–3 years</option>
                                        <option value="More than 3 years">
                                            More than 3 years
                                        </option>
                                    </select>
                                </div>

                                <div>
                                    <label
                                        htmlFor="reg-day"
                                        className="mb-2 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/60"
                                    >
                                        <CalendarDays
                                            size={12}
                                            className="text-neon"
                                        />
                                        Hari Webinar Pilihan *
                                    </label>
                                    <select
                                        id="reg-day"
                                        name="preferred_day"
                                        required
                                        defaultValue=""
                                        data-testid="registration-day-select"
                                        className={`${inputCls} cursor-pointer [&>option]:bg-[#141020]`}
                                    >
                                        <option value="" disabled>
                                            Pilih hari
                                        </option>
                                        <option value="Monday">Monday</option>
                                        <option value="Tuesday">Tuesday</option>
                                        <option value="Either Day">
                                            Either Day
                                        </option>
                                    </select>
                                </div>

                                <label className="flex cursor-pointer items-start gap-3 text-left">
                                    <input
                                        type="checkbox"
                                        required
                                        data-testid="registration-consent-checkbox"
                                        className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-[#A020F0]"
                                    />
                                    <span className="text-xs leading-relaxed text-white/60">
                                        Saya bersetuju maklumat saya digunakan
                                        untuk pendaftaran webinar ini mengikut{" "}
                                        <a
                                            href="#/privacy-policy"
                                            data-testid="registration-privacy-link"
                                            className="text-neon underline underline-offset-2"
                                        >
                                            Privacy Policy
                                        </a>
                                        . *
                                    </span>
                                </label>

                                {state === "error" && errorMsg && (
                                    <p
                                        data-testid="registration-error"
                                        className="rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-xs text-[#FF8A8E]"
                                    >
                                        {errorMsg}
                                    </p>
                                )}

                                <button
                                    type="submit"
                                    disabled={state === "sending"}
                                    data-testid="registration-submit-button"
                                    className="inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-full bg-gradient-to-b from-[#B44BFF] to-[#8B1FD6] px-8 py-4 font-display text-sm font-extrabold uppercase tracking-wide text-white shadow-[0_12px_45px_-10px_rgba(160,32,240,0.7)] transition-colors duration-300 hover:from-[#c466ff] hover:to-[#9a2fe6] active:scale-[0.99] disabled:cursor-wait disabled:opacity-70"
                                >
                                    {state === "sending" ? (
                                        <>
                                            <Loader2
                                                size={16}
                                                className="animate-spin"
                                            />
                                            Menghantar...
                                        </>
                                    ) : (
                                        "HANTAR PENDAFTARAN"
                                    )}
                                </button>

                                <p className="text-center text-[11px] text-white/40">
                                    Borang ni pilihan sahaja — anda juga boleh
                                    PM admin terus di Telegram.
                                </p>
                            </form>
                        )}
                    </div>
                </Reveal>
            </div>
        </section>
    );
};

export default RegistrationSection;
